/**
 * Deal Quote Service
 *
 * Manages quote creation and service plan integration for deals
 *
 * Features:
 * - Convert service plans from intake to quote items
 * - Calculate recurring vs one-time totals
 * - Auto-add service plans to quotes when creating quote versions
 */

import type { ServicePlan, PlaygroundQuoteItem } from '@/types/schema';
import type { QuoteVersion, Deal } from '@/types/theo-workflow.types';
import { DealCategory } from '@/types/theo-workflow.types';
import { getActiveServicePlans } from '@/services/service-plans.service';
import { DEFAULT_GST_RATE, itemTotal, roundMoney } from '@/services/quote-pricing';
import { dealModuleKeyForPlanType } from '@shared/proposals/plan-module';

/**
 * Extract service plans from deal's service modules.
 * Returns array of {planName, serviceType, isInstallationOnly}.
 *
 * For services with a servicePlan intake field (NBN, Starlink): extracts the plan name.
 * For installation-only services (WiFi Extension, Security, Smart Home, IT):
 *   these don't have a servicePlan field, so we generate a descriptive name
 *   from the service type and mark them as installation-only.
 */
export function extractServicePlansFromDeal(deal: Deal): Array<{
  planName: string;
  serviceType: DealCategory;
  isInstallationOnly?: boolean;
}> {
  const plans: Array<{ planName: string; serviceType: DealCategory; isInstallationOnly?: boolean }> = [];

  if (!deal.selectedServices || !deal.serviceModules) {
    return plans;
  }

  // Installation-only categories that don't have a servicePlan intake field
  const INSTALLATION_CATEGORIES: DealCategory[] = [
    DealCategory.WIFI_EXTENSION,
    DealCategory.SECURITY,
    DealCategory.ACCESS_CONTROL,
    DealCategory.AV,
    DealCategory.SMART_HOME,
    DealCategory.IT,
  ];

  const CATEGORY_LABELS: Record<string, string> = {
    WIFI_EXTENSION: 'WiFi Extension',
    SECURITY: 'Surveillance',
    ACCESS_CONTROL: 'Access Control',
    AV: 'AV',
    SMART_HOME: 'Smart Home',
    IT: 'IT',
    VOIP: 'VoIP',
    FEE_FOR_SERVICE: 'Fee For Service',
  };

  for (const serviceType of deal.selectedServices) {
    const moduleData = deal.serviceModules[serviceType];
    if (!moduleData) continue;

    const intakeFields = (moduleData.intakeFields || {}) as unknown as Record<string, unknown>;
    const servicePlan = intakeFields.servicePlan as string | undefined;

    if (servicePlan && servicePlan.trim() !== '') {
      // Plan-based service (NBN, Starlink, etc.)
      plans.push({
        planName: servicePlan,
        serviceType,
      });
    } else if (INSTALLATION_CATEGORIES.includes(serviceType)) {
      // Installation-only service — create a service record even without a plan
      const label = CATEGORY_LABELS[serviceType] || serviceType;
      plans.push({
        planName: `${label} Installation`,
        serviceType,
        isInstallationOnly: true,
      });
    }
  }

  return plans;
}

/**
 * Convert a service plan to a quote item
 */
export function servicePlanToQuoteItem(
  servicePlan: ServicePlan,
  serviceType: DealCategory
): PlaygroundQuoteItem {
  // Respect the plan's own billing type (2026-07 pricing audit —
  // CRITICAL). This used to hardcode 'recurring', so a ONE-TIME plan
  // (hardware kit / installation package sold through the plan picker)
  // landed in the customer's MONTHLY figure — inflating "per month" by
  // the kit price — while vanishing from the one-time investment total.
  // ServicePlan.billingType defaults to 'recurring' when unset.
  const isOneTime = servicePlan.billingType === 'one-time';
  return {
    id: crypto.randomUUID(),
    name: servicePlan.displayName,
    sku: servicePlan.sku,
    category: 'Service Plans',
    quantity: 1,
    unit: isOneTime ? 'each' : 'month',
    unitCost: servicePlan.costPrice,
    markup: servicePlan.margin ? servicePlan.margin / 100 : 0,
    unitPrice: servicePlan.sellPrice,
    totalPrice: servicePlan.sellPrice,
    sourceType: 'manual',
    pipelineStatus: 'draft',
    billingType: isOneTime ? 'one-time' : 'recurring',
    serviceModule: serviceType,
    isCustom: false,
    // S5b: the line remembers WHICH plan it is (billing matches by id).
    ...(servicePlan.id ? { servicePlanId: servicePlan.id } : {}),
    notes: servicePlan.features?.join(', '),
    addedAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

/**
 * Create quote items from service plans selected in intake.
 *
 * Looks up each plan in Firestore by displayName/planName to get real pricing.
 * Falls back to a $0-priced placeholder if the plan can't be found.
 *
 * @param deal - The deal with selectedServices and serviceModules
 * @param tenantId - Tenant ID for Firestore queries
 */
export async function createQuoteItemsFromServicePlans(
  deal: Deal,
  tenantId?: string
): Promise<PlaygroundQuoteItem[]> {
  const extractedPlans = extractServicePlansFromDeal(deal);
  const quoteItems: PlaygroundQuoteItem[] = [];

  // Nothing to resolve — skip the service_plans fetch entirely. The loop
  // below would produce [] anyway, so this just avoids a wasted Firestore
  // read for the common straight-to-quoting deal with no recurring plan.
  if (extractedPlans.length === 0) return quoteItems;

  // Fetch all active service plans once to match against
  let allPlans: ServicePlan[] = [];
  if (tenantId) {
    try {
      allPlans = await getActiveServicePlans(tenantId);
    } catch (err) {
      console.error('[deal-quote] Failed to fetch service plans:', err);
    }
  }

  // Build lookup maps for matching by displayName and planName (case-insensitive)
  const plansByDisplayName = new Map<string, ServicePlan>();
  const plansByPlanName = new Map<string, ServicePlan>();
  for (const plan of allPlans) {
    if (plan.displayName) plansByDisplayName.set(plan.displayName.toLowerCase(), plan);
    if (plan.planName) plansByPlanName.set(plan.planName.toLowerCase(), plan);
  }

  for (const { planName, serviceType, isInstallationOnly } of extractedPlans) {
    // Installation-only services don't have recurring plans
    if (isInstallationOnly) continue;

    try {
      // Look up plan by displayName first, then planName
      const key = planName.toLowerCase();
      const matchedPlan = plansByDisplayName.get(key) || plansByPlanName.get(key);

      if (matchedPlan) {
        // Found in Firestore — use real pricing
        quoteItems.push(servicePlanToQuoteItem(matchedPlan, serviceType));
      } else {
        // No match — create placeholder with plan name, $0 pricing for manual entry
        quoteItems.push({
          id: crypto.randomUUID(),
          name: planName,
          category: 'Service Plans',
          quantity: 1,
          unit: 'month',
          unitCost: 0,
          unitPrice: 0,
          totalPrice: 0,
          sourceType: 'manual',
          pipelineStatus: 'draft',
          billingType: 'recurring',
          serviceModule: serviceType,
          isCustom: false,
          notes: `Plan "${planName}" not found in service plans — check pricing`,
          addedAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        });
      }
    } catch (error) {
      console.error(`Failed to create quote item for plan "${planName}":`, error);
    }
  }

  return quoteItems;
}

/**
 * Calculate totals for a quote separating one-time and recurring.
 *
 * CANONICAL SEMANTICS (2026-06 audit fix H4 — see quote-pricing.ts):
 *   - per-item money is recomputed as `unitPrice × quantity` (stored
 *     `totalPrice` is an untrusted cache)
 *   - `subtotal` / `gst` / `total` are ONE-TIME only — recurring is never
 *     folded into a one-time number and never one-time-GST'd
 *   - `monthlyTotal` is the recurring figure inc-GST (display convenience
 *     for legacy QuoteVersion consumers); `recurringSubtotal` is ex-GST
 */
export function calculateQuoteTotals(items: PlaygroundQuoteItem[]): {
  oneTimeSubtotal: number;
  recurringSubtotal: number; // Monthly recurring, ex-GST
  subtotal: number; // One-time only, ex-GST (recurring EXCLUDED — H4)
  gst: number; // GST on the one-time subtotal only
  total: number; // One-time total with GST
  monthlyTotal: number; // Monthly recurring with GST
} {
  let oneTimeSubtotal = 0;
  let recurringSubtotal = 0;

  for (const item of items) {
    if (item.billingType === 'recurring') {
      recurringSubtotal += itemTotal(item);
    } else {
      oneTimeSubtotal += itemTotal(item);
    }
  }
  oneTimeSubtotal = roundMoney(oneTimeSubtotal);
  recurringSubtotal = roundMoney(recurringSubtotal);

  // TODO(saas-readiness): GST hardcoded via DEFAULT_GST_RATE (the ONE place
  // the 0.10 literal lives — quote-pricing.ts) — should read
  // tenant.business.defaultTaxRate. Deferred as a dedicated money task: GST is
  // computed in parallel server-side (functions/), so client + server must move
  // together or quote/invoice totals diverge for a non-AU tenant. Can't be
  // runtime-validated here. The defaultTaxRate field already exists; this and
  // the other ~7 client GST sites (billing cards, AddLineItemsModal,
  // VariationReviewDocument.gst, nbn-reconciliation) convert as one unit.
  const subtotal = oneTimeSubtotal;
  const gst = roundMoney(oneTimeSubtotal * DEFAULT_GST_RATE);
  const total = roundMoney(oneTimeSubtotal + gst); // One-time total with GST
  const monthlyTotal = roundMoney(
    recurringSubtotal * (1 + DEFAULT_GST_RATE)
  ); // Recurring with GST

  return {
    oneTimeSubtotal,
    recurringSubtotal,
    subtotal,
    gst,
    total,
    monthlyTotal,
  };
}

/**
 * Create a new quote version with service plans included
 */
export function createQuoteVersion(
  items: PlaygroundQuoteItem[],
  version: number,
  status: QuoteVersion['status'] = 'draft'
): QuoteVersion {
  const totals = calculateQuoteTotals(items);

  return {
    version,
    items,
    subtotal: totals.subtotal,
    gst: totals.gst,
    total: totals.total,
    monthlyTotal: totals.monthlyTotal,
    status,
    createdAt: new Date().toISOString(),
  };
}

/**
 * Check if a quote has any recurring items
 */
export function hasRecurringItems(items: PlaygroundQuoteItem[]): boolean {
  return items.some((item) => item.billingType === 'recurring');
}

/**
 * Get all recurring items from a quote
 */
export function getRecurringItems(items: PlaygroundQuoteItem[]): PlaygroundQuoteItem[] {
  return items.filter((item) => item.billingType === 'recurring');
}

/**
 * Get all one-time items from a quote
 */
export function getOneTimeItems(items: PlaygroundQuoteItem[]): PlaygroundQuoteItem[] {
  return items.filter((item) => item.billingType !== 'recurring');
}

/**
 * Integration Helper: resolve service plans for a deal into
 * `PlaygroundQuoteItem[]` ready to land in a "Service Plans" scope.
 *
 * Today's primary caller is the unified `ProposalBuilderPage` — both
 * the AI scope auto-import path (via `runFieldScopeMatcher`) and the
 * `FieldScopeImportModal`-era flow before it (now removed) call this
 * to enrich the imported scopes with the recurring plans defined on
 * the deal's intake.
 *
 * Example:
 * ```typescript
 * import { addServicePlansToQuote } from '@/services/deal-quote.service';
 *
 * const items = await addServicePlansToQuote(deal, tenantId);
 * // …drop `items` into the auto-created "Service Plans" scope
 * ```
 */
export async function addServicePlansToQuote(deal: Deal, tenantId?: string): Promise<PlaygroundQuoteItem[]> {
  return await createQuoteItemsFromServicePlans(deal, tenantId);
}

// ─────────────────────────────────────────────────────────────────────
// Billing-time service extraction
// ─────────────────────────────────────────────────────────────────────

export interface BillingServiceCandidate {
  serviceType: DealCategory;     // 'NBN' | 'STARLINK' | 'WIFI_EXTENSION' | ...
  planName: string;              // Best label we have (item name or "{Label} Installation")
  billingType: 'recurring' | 'one-time';
  unitPrice?: number;            // Monthly price for recurring, install price for one-time (ex-GST)
  isInstallationOnly?: boolean;
  /** S5b: the plan the recurring line was picked from — billing matches it by id first. */
  servicePlanId?: string;
}

const INSTALLATION_CATEGORIES: DealCategory[] = [
  DealCategory.WIFI_EXTENSION,
  DealCategory.SECURITY,
  DealCategory.ACCESS_CONTROL,
  DealCategory.AV,
  DealCategory.SMART_HOME,
  DealCategory.IT,
];

const CATEGORY_LABELS: Record<string, string> = {
  WIFI_EXTENSION: 'WiFi Extension',
  SECURITY: 'Surveillance',
  ACCESS_CONTROL: 'Access Control',
  AV: 'AV',
  SMART_HOME: 'Smart Home',
  IT: 'IT',
  VOIP: 'VoIP',
  NBN: 'NBN',
  STARLINK: 'Starlink',
  FEE_FOR_SERVICE: 'Fee For Service',
};

// Heuristic keyword → DealCategory match. Used when a quote item doesn't
// carry an explicit `serviceModule` (e.g. items synced from Better
// Proposals). Order matters — more specific keywords first.
const KEYWORD_RULES: Array<{ patterns: RegExp[]; category: DealCategory }> = [
  { patterns: [/\bstarlink\b/i, /\bsl\s*dish\b/i], category: DealCategory.STARLINK },
  { patterns: [/\bnbn\b/i, /\bntd\b/i, /national broadband/i], category: DealCategory.NBN },
  { patterns: [/\bvoip\b/i, /\bsip\b/i, /handset/i, /phone\s*line/i, /\bata\b/i], category: DealCategory.VOIP },
  { patterns: [/access\s*point/i, /\bunifi\b/i, /\bdeco\b/i, /\bmesh\b/i, /\bwifi\b/i, /wi-fi/i, /switch/i], category: DealCategory.WIFI_EXTENSION },
  { patterns: [/camera/i, /\bnvr\b/i, /\bcctv\b/i, /doorbell/i, /intercom/i, /alarm/i], category: DealCategory.SECURITY },
  { patterns: [/smart\s*lock/i, /smart\s*light/i, /relay/i, /automation/i, /smart\s*switch/i], category: DealCategory.SMART_HOME },
  { patterns: [/\bav\b/i, /audio\s*visual/i, /home\s*theatre/i, /home\s*theater/i, /soundbar/i, /amplifier/i, /projector/i, /tv\s*mount/i], category: DealCategory.AV },
  { patterns: [/\bserver\b/i, /workstation/i, /firewall/i, /\bups\b/i, /\brack\b/i], category: DealCategory.IT },
];

function inferCategory(itemName: string, explicitModule?: string): DealCategory | null {
  if (explicitModule) {
    // serviceModule is stored as a DealCategory string ('NBN', 'STARLINK', ...) — or, on a plan-linked
    // line (S5b / R11), as the plan's serviceType ('Starlink', 'Smart Home'): its module key first.
    const upper = (dealModuleKeyForPlanType(explicitModule) ?? explicitModule).toUpperCase();
    const match = (Object.values(DealCategory) as string[]).find(v => v === upper);
    if (match) return match as DealCategory;
  }
  if (!itemName) return null;
  for (const rule of KEYWORD_RULES) {
    if (rule.patterns.some(p => p.test(itemName))) return rule.category;
  }
  return null;
}

/**
 * Extract billing-ready service candidates from the deal's quote data.
 *
 * Walks the same waterfall the BillingPage line items use:
 *   1. quoteReviewedItems (approved only — most authoritative)
 *   2. quoteScopes (flatten all items)
 *   3. quoteVersions[latest].scopes / items
 *   4. proposalAcceptedItems (Better Proposals fallback)
 *
 * Items are grouped by inferred service category (DealCategory). For each
 * category:
 *   - if any recurring item exists → ONE recurring entry (uses the recurring
 *     item's name + unit price as monthly)
 *   - else if any one-time item exists for an installation category →
 *     ONE installation-only entry
 *
 * Falls back to `extractServicePlansFromDeal()` when the quote-side
 * extraction produces nothing — keeps legacy intake-only deals working.
 */
/** R11: a reviewed line's service module, when acceptance carried one. */
function reviewedModule(ri: object): string | undefined {
  const v = (ri as { serviceModule?: unknown }).serviceModule;
  return typeof v === 'string' && v.trim() ? v : undefined;
}

export function extractBillingServicesFromDeal(deal: Deal): BillingServiceCandidate[] {
  type RawItem = {
    name: string;
    serviceModule?: string;
    billingType?: string;
    unitPrice?: number;
    /** S5b: the plan link (set by the plan pickers, carried through acceptance). */
    servicePlanId?: string;
  };

  // Step 1: pull the best available item list
  let items: RawItem[] = [];

  if (deal.quoteReviewedItems && deal.quoteReviewedItems.length > 0) {
    items = deal.quoteReviewedItems
      .filter(ri => ri.approved)
      .map(ri => ({
        name: ri.name,
        // R11: onProposalAccepted carries the signed line's serviceModule onto
        // the reviewed item (not on ReviewedQuoteItem's type yet) — a plan's
        // category is then never guessed from its name; older reviewed items
        // fall back to the keyword inference below.
        serviceModule: reviewedModule(ri),
        billingType: ri.billingType,
        unitPrice: ri.revisedUnitPrice ?? ri.originalUnitPrice,
        servicePlanId: ri.servicePlanId,
      }));
  }

  if (items.length === 0 && deal.quoteScopes && deal.quoteScopes.length > 0) {
    for (const scope of deal.quoteScopes) {
      for (const it of scope.items || []) {
        items.push({
          name: it.name,
          serviceModule: it.serviceModule,
          billingType: it.billingType,
          unitPrice: it.unitPrice,
          servicePlanId: it.servicePlanId,
        });
      }
    }
  }

  if (items.length === 0 && deal.quoteVersions && deal.quoteVersions.length > 0) {
    const latest = deal.quoteVersions[deal.quoteVersions.length - 1];
    const flat = latest.scopes
      ? latest.scopes.flatMap(s => s.items || [])
      : latest.items || [];
    for (const it of flat) {
      items.push({
        name: it.name,
        serviceModule: it.serviceModule,
        billingType: it.billingType,
        unitPrice: it.unitPrice,
        servicePlanId: it.servicePlanId,
      });
    }
  }

  if (items.length === 0 && deal.proposalAcceptedItems && deal.proposalAcceptedItems.length > 0) {
    for (const ai of deal.proposalAcceptedItems) {
      if (ai.isSelected === false) continue;
      const recurring = ai.recurringType && ai.recurringType !== 'One Time Payment';
      items.push({
        name: ai.label || '',
        billingType: recurring ? 'recurring' : 'one-time',
        unitPrice: recurring ? (ai.monthlyCost || ai.unitCost) : ai.unitCost,
      });
    }
  }

  // Step 2: group by inferred category, prefer recurring over one-time
  type Group = { recurring?: RawItem; oneTime?: RawItem };
  const grouped = new Map<DealCategory, Group>();

  for (const it of items) {
    const category = inferCategory(it.name, it.serviceModule);
    if (!category) continue; // un-categorisable line items (labour, generic gear) — skip

    const grp = grouped.get(category) || {};
    if (it.billingType === 'recurring') {
      // Pick the most expensive recurring as the primary plan (handles
      // duplicate or split-out items)
      if (!grp.recurring || (it.unitPrice ?? 0) > (grp.recurring.unitPrice ?? 0)) {
        grp.recurring = it;
      }
    } else {
      if (!grp.oneTime) grp.oneTime = it;
    }
    grouped.set(category, grp);
  }

  const candidates: BillingServiceCandidate[] = [];
  for (const [category, grp] of grouped.entries()) {
    if (grp.recurring) {
      candidates.push({
        serviceType: category,
        planName: grp.recurring.name || `${CATEGORY_LABELS[category] || category} Plan`,
        billingType: 'recurring',
        unitPrice: grp.recurring.unitPrice,
        ...(grp.recurring.servicePlanId ? { servicePlanId: grp.recurring.servicePlanId } : {}),
      });
    } else if (grp.oneTime && INSTALLATION_CATEGORIES.includes(category)) {
      const label = CATEGORY_LABELS[category] || category;
      candidates.push({
        serviceType: category,
        planName: `${label} Installation`,
        billingType: 'one-time',
        unitPrice: grp.oneTime.unitPrice,
        isInstallationOnly: true,
      });
    }
  }

  // Step 3: also surface intake-only categories that have NO quote items
  // (legacy deals + deals where intake captured a plan but quote-side data
  // is missing). Skip categories we already have from the quote side.
  const intakeFallback = extractServicePlansFromDeal(deal);
  for (const f of intakeFallback) {
    if (candidates.some(c => c.serviceType === f.serviceType)) continue;
    candidates.push({
      serviceType: f.serviceType,
      planName: f.planName,
      billingType: f.isInstallationOnly ? 'one-time' : 'recurring',
      isInstallationOnly: f.isInstallationOnly,
    });
  }

  return candidates;
}
